import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * Gallery source priority on every variant change:
 * 1. Selected variant parajohn.variant_gallery_images
 * 2. Product Color Gallery (product + color)
 * 3. Default product.media
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @typedef {{ color?: string, ids: string[] }} PjVariantGalleryEntry
 * @typedef {{ ids: string[], variantIds?: string[], orderedIds?: string[] }} PjColorGalleryEntry
 * @typedef {{
 *   productId: string|number,
 *   displayMode: string,
 *   defaultMediaIds: string[],
 *   shopifyFeaturedMedia: Record<string, string|number>,
 *   variants: Record<string, PjVariantGalleryEntry>,
 *   colors: Record<string, PjColorGalleryEntry>,
 *   allMediaIds?: string[]
 * }} PjGalleryData
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  /** @type {PjGalleryData | null} */
  #galleryData = null;

  /** @type {string | null} */
  #lastGalleryKey = null;

  /** @type {boolean} */
  #clientApplied = false;

  connectedCallback() {
    super.connectedCallback();

    this.#galleryData = this.#readGalleryData();
    this.#lastGalleryKey = this.dataset.pjGalleryKey || this.dataset.pjColorGalleryKey || null;

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // Sync Random order (and re-assert selected source) from Liquid JSON without waiting for a change
    queueMicrotask(() => this.#syncInitialGallery());

    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(() => this.#preloadHiddenMedia(), { timeout: 2000 });
    } else {
      setTimeout(() => this.#preloadHiddenMedia(), 500);
    }
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Apply resolved gallery once on connect (Random reorder / correctness).
   */
  #syncInitialGallery() {
    const variantId = this.dataset.pjSelectedVariantId || null;
    const color = this.dataset.pjSelectedColor || null;
    const resolved = this.#resolveGallery(variantId, color);
    if (!resolved) return;

    const displayMode = this.#galleryData?.displayMode || this.dataset.pjVariantImageDisplay || '';
    const needsSync =
      displayMode === 'random' ||
      (this.#lastGalleryKey && this.#lastGalleryKey !== resolved.key);

    if (!needsSync && this.#lastGalleryKey === resolved.key) return;

    if (this.#applyGalleryFast(resolved)) {
      this.#lastGalleryKey = resolved.key;
    }
  }

  /**
   * @returns {PjGalleryData | null}
   */
  #readGalleryData() {
    const script = this.querySelector('script[type="application/json"][data-pj-color-gallery-data]');
    if (!(script instanceof HTMLScriptElement) || !script.textContent) return null;

    try {
      const data = JSON.parse(script.textContent);
      if (!data || typeof data !== 'object') return null;
      return {
        productId: data.productId,
        displayMode: data.displayMode || 'variant_image_first',
        defaultMediaIds: Array.isArray(data.defaultMediaIds)
          ? data.defaultMediaIds.map(String)
          : Array.isArray(data.allMediaIds)
            ? data.allMediaIds.map(String)
            : [],
        shopifyFeaturedMedia:
          data.shopifyFeaturedMedia && typeof data.shopifyFeaturedMedia === 'object'
            ? data.shopifyFeaturedMedia
            : {},
        variants: data.variants && typeof data.variants === 'object' ? data.variants : {},
        colors: data.colors && typeof data.colors === 'object' ? data.colors : {},
      };
    } catch {
      return null;
    }
  }

  /**
   * @param {ProductSelectEvent} event
   * @returns {string | null}
   */
  #getSelectedColor(event) {
    const options = event.selectedOptions ?? [];
    for (const option of options) {
      const name = option?.name?.trim()?.toLowerCase?.() ?? '';
      if (name === 'color' || name === 'colour' || name === 'colors' || name === 'colours') {
        return option.value?.trim?.() || null;
      }
    }
    return null;
  }

  /**
   * @param {ProductSelectEvent} event
   * @returns {string | null}
   */
  #getSelectedVariantId(event) {
    const fromDetail = event.detail?.variantId;
    if (fromDetail != null && String(fromDetail) !== '') {
      return this.#normalizeVariantId(fromDetail);
    }

    const resourceId = event.detail?.resource?.id;
    if (resourceId != null && String(resourceId) !== '') {
      return this.#normalizeVariantId(resourceId);
    }

    return null;
  }

  /**
   * @param {string | number} id
   * @returns {string | null}
   */
  #normalizeVariantId(id) {
    const raw = String(id).trim();
    if (!raw) return null;
    if (raw.includes('/')) {
      const tail = raw.split('/').pop();
      return tail || null;
    }
    return raw;
  }

  /**
   * @param {string} color
   * @returns {string[]}
   */
  #getColorGalleryIds(color) {
    const data = this.#galleryData;
    if (!data?.colors || !color) return [];

    const direct = data.colors[color];
    if (direct) return this.#entryIds(direct);

    const needle = color.trim().toLowerCase();
    for (const [key, entry] of Object.entries(data.colors)) {
      if (key.trim().toLowerCase() === needle) return this.#entryIds(entry);
    }
    return [];
  }

  /**
   * @param {PjColorGalleryEntry | PjVariantGalleryEntry | null | undefined} entry
   * @returns {string[]}
   */
  #entryIds(entry) {
    if (!entry) return [];
    if (Array.isArray(entry.ids) && entry.ids.length) return entry.ids.map(String);
    // Legacy shape from older color maps
    if ('variantIds' in entry && Array.isArray(entry.variantIds) && entry.variantIds.length) {
      return entry.variantIds.map(String);
    }
    if ('orderedIds' in entry && Array.isArray(entry.orderedIds) && entry.orderedIds.length) {
      return entry.orderedIds.map(String);
    }
    return [];
  }

  /**
   * Resolve gallery source for the selected variant.
   * Priority: variant images → color gallery → default product.media
   *
   * @param {string | null} variantId
   * @param {string | null} color
   * @returns {{ source: 'variant' | 'color' | 'default', ids: string[], key: string } | null}
   */
  #resolveGallery(variantId, color) {
    const data = this.#galleryData ?? this.#readGalleryData();
    this.#galleryData = data;
    if (!data) return null;

    const productId = String(data.productId ?? '');

    if (variantId && data.variants?.[variantId]) {
      const ids = this.#entryIds(data.variants[variantId]);
      if (ids.length) {
        return {
          source: 'variant',
          ids: this.#orderIds(ids, data.displayMode, `${productId}:v:${variantId}`),
          key: `${productId}::variant::${variantId}::${data.displayMode}`,
        };
      }
    }

    if (color) {
      const ids = this.#getColorGalleryIds(color);
      if (ids.length) {
        return {
          source: 'color',
          ids: this.#orderIds(ids, data.displayMode, `${productId}:c:${color}`),
          key: `${productId}::color::${color}::${data.displayMode}`,
        };
      }
    }

    const defaultIds = (data.defaultMediaIds || []).map(String);
    if (defaultIds.length) {
      const featuredRaw =
        (variantId && data.shopifyFeaturedMedia?.[variantId]) ||
        null;
      const featuredId = featuredRaw != null ? String(featuredRaw) : null;
      let ids = defaultIds;
      if (featuredId && defaultIds.includes(featuredId)) {
        ids = [featuredId, ...defaultIds.filter((id) => id !== featuredId)];
      }
      return {
        source: 'default',
        ids,
        key: `${productId}::default::${variantId || 'none'}::${featuredId || 'none'}::${data.displayMode}`,
      };
    }

    return null;
  }

  /**
   * Random mode rotates the selected source only — never mixes lower-priority media.
   * @param {string[]} ids
   * @param {string} displayMode
   * @param {string} seedKey
   * @returns {string[]}
   */
  #orderIds(ids, displayMode, seedKey) {
    if (displayMode !== 'random' || ids.length <= 1) return ids;

    let seed = 0;
    for (let i = 0; i < seedKey.length; i++) seed = (seed * 31 + seedKey.charCodeAt(i)) >>> 0;
    const rotateBy = seed % ids.length;
    if (!rotateBy) return ids;

    return ids.map((_, index) => ids[(index + rotateBy) % ids.length]);
  }

  /**
   * Instantly show the resolved gallery source by reordering existing nodes.
   * @param {{ source: string, ids: string[], key: string }} resolved
   * @returns {boolean}
   */
  #applyGalleryFast(resolved) {
    const orderedIds = resolved.ids.map(String);
    const visibleSet = new Set(orderedIds);

    if (!this.#reorderSlideshow(orderedIds, visibleSet)) {
      return false;
    }
    this.#reorderGrid(orderedIds, visibleSet);
    this.#reorderThumbnails(orderedIds);
    this.#updateZoomIndexes(orderedIds.length);
    this.#updateSlideshowChrome(orderedIds.length);

    this.dataset.pjGalleryKey = resolved.key;
    this.dataset.pjColorGalleryKey = resolved.key;
    this.dataset.pjGallerySource = resolved.source;
    this.setAttribute('data-pj-color-gallery-fast', '');

    const slideshow = this.slideshow;
    if (slideshow && typeof slideshow.select === 'function') {
      const firstId = orderedIds[0];
      queueMicrotask(() => {
        try {
          slideshow.select({ id: firstId }, undefined, { animate: false });
        } catch {
          try {
            slideshow.select(0, undefined, { animate: false });
          } catch {
            /* ignore */
          }
        }
      });
    }

    this.#lastGalleryKey = resolved.key;
    return true;
  }

  /**
   * @param {number} visibleCount
   */
  #updateSlideshowChrome(visibleCount) {
    const slideshow = this.querySelector('slideshow-component');
    if (slideshow instanceof HTMLElement) {
      slideshow.style.setProperty('--slideshow-total-slides', String(Math.max(visibleCount, 1)));
    }

    const single = visibleCount <= 1;
    this.querySelectorAll('slideshow-component').forEach((el) => {
      el.classList.toggle('product-media-gallery__slideshow--single-media', single);
      el.classList.toggle('slideshow--single-media', single);
    });
  }

  /**
   * @param {string[]} orderedIds
   * @param {Set<string>} visibleSet
   * @returns {boolean} false if any required media node is missing
   */
  #reorderSlideshow(orderedIds, visibleSet) {
    const scroller = this.querySelector('slideshow-slides');
    if (!scroller) return false;

    /** @type {Map<string, Element>} */
    const byId = new Map();
    for (const slide of scroller.querySelectorAll(':scope > slideshow-slide[data-pj-media-id]')) {
      const id = String(slide.getAttribute('data-pj-media-id'));
      if (!byId.has(id)) byId.set(id, slide);
    }

    for (const id of orderedIds) {
      if (!byId.has(String(id))) return false;
    }

    const fragment = document.createDocumentFragment();
    let index = 0;
    for (const id of orderedIds) {
      const slide = byId.get(String(id));
      if (!slide) continue;
      slide.removeAttribute('hidden');
      slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
      this.#patchTimeline(slide, index);
      fragment.appendChild(slide);
      byId.delete(String(id));
      index += 1;
    }

    for (const slide of byId.values()) {
      slide.setAttribute('hidden', '');
      slide.setAttribute('aria-hidden', 'true');
      fragment.appendChild(slide);
    }

    scroller.appendChild(fragment);
    return true;
  }

  /**
   * @param {Element} slide
   * @param {number} index
   */
  #patchTimeline(slide, index) {
    if (!(slide instanceof HTMLElement)) return;
    const style = slide.getAttribute('style') || '';
    const next = style.replace(/--slideshow-timeline:\s*--slide-\d+/, `--slideshow-timeline: --slide-${index}`);
    if (next !== style) slide.setAttribute('style', next);
  }

  /**
   * @param {string[]} orderedIds
   * @param {Set<string>} _visibleSet
   */
  #reorderGrid(orderedIds, _visibleSet) {
    const grid = this.querySelector('.media-gallery__grid');
    if (!grid) return;

    /** @type {Map<string, Element>} */
    const byId = new Map();
    for (const item of grid.querySelectorAll(':scope > li[data-pj-media-id]')) {
      byId.set(String(item.getAttribute('data-pj-media-id')), item);
    }

    const fragment = document.createDocumentFragment();
    let index = 0;
    for (const id of orderedIds) {
      const item = byId.get(id);
      if (!item) continue;
      item.removeAttribute('hidden');
      item.classList.remove('media-gallery__grid-item--deferred');
      this.#patchGridZoom(item, index);
      fragment.appendChild(item);
      byId.delete(id);
      index += 1;
    }

    for (const item of byId.values()) {
      item.setAttribute('hidden', '');
      fragment.appendChild(item);
    }

    grid.appendChild(fragment);
  }

  /**
   * @param {Element} item
   * @param {number} index
   */
  #patchGridZoom(item, index) {
    const button = item.querySelector('button.product-media-container__zoom-button');
    if (!(button instanceof HTMLElement)) return;
    button.setAttribute('on:click', `/zoom/${index}`);
    if (button.getAttribute('on:pointerenter')) {
      button.setAttribute('on:pointerenter', `/preloadImage/${index}`);
    }
  }

  /**
   * @param {string[]} orderedIds
   */
  #reorderThumbnails(orderedIds) {
    const containers = this.querySelectorAll('.slideshow-controls__thumbnails');
    for (const container of containers) {
      /** @type {Map<string, Element>} */
      const byId = new Map();
      for (const thumb of container.querySelectorAll(':scope > .slideshow-controls__thumbnail')) {
        const mediaId =
          thumb.getAttribute('data-pj-media-id') ||
          thumb.querySelector('img')?.getAttribute('data-pj-media-id') ||
          '';
        if (mediaId) byId.set(String(mediaId), thumb);
      }

      if (byId.size === 0) {
        this.#reorderThumbnailsByPosition(container, orderedIds.length);
        continue;
      }

      const fragment = document.createDocumentFragment();
      let index = 0;
      for (const id of orderedIds) {
        const thumb = byId.get(id);
        if (!thumb) continue;
        thumb.removeAttribute('hidden');
        thumb.setAttribute('on:click', `/select/${index}`);
        thumb.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
        fragment.appendChild(thumb);
        byId.delete(id);
        index += 1;
      }
      for (const thumb of byId.values()) {
        thumb.setAttribute('hidden', '');
        fragment.appendChild(thumb);
      }
      container.appendChild(fragment);
    }
  }

  /**
   * Fallback when thumbnails have no media ids: keep the first N thumbs visible and re-index.
   * @param {Element} container
   * @param {number} visibleCount
   */
  #reorderThumbnailsByPosition(container, visibleCount) {
    const thumbs = [...container.querySelectorAll(':scope > .slideshow-controls__thumbnail')];
    thumbs.forEach((thumb, index) => {
      if (index < visibleCount) {
        thumb.removeAttribute('hidden');
        thumb.setAttribute('on:click', `/select/${index}`);
        thumb.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
      } else {
        thumb.setAttribute('hidden', '');
      }
    });
  }

  /**
   * @param {number} _visibleCount
   */
  #updateZoomIndexes(_visibleCount) {
    const slides = this.querySelectorAll('slideshow-slides > slideshow-slide:not([hidden])');
    slides.forEach((slide, index) => {
      if (!(slide instanceof HTMLElement)) return;
      const click = slide.getAttribute('on:click');
      if (click && click.includes('/open/')) {
        slide.setAttribute('on:click', click.replace(/\/open\/\d+/, `/open/${index}`));
      }
    });
  }

  /**
   * Warm cache for off-source media so the next switch paints immediately.
   */
  #preloadHiddenMedia() {
    const images = this.querySelectorAll(
      'slideshow-slide[hidden] img, .media-gallery__grid > li[hidden] img'
    );
    for (const img of images) {
      if (!(img instanceof HTMLImageElement)) continue;
      const src = img.currentSrc || img.src;
      if (!src) continue;
      const warm = new Image();
      warm.decoding = 'async';
      warm.src = src;
    }
  }

  /**
   * Handles a product select event.
   * Resolves gallery by variant → color → default; applies instantly when nodes exist.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const color = this.#getSelectedColor(event);
    const variantId = this.#getSelectedVariantId(event);
    this.#clientApplied = false;

    const resolved = this.#resolveGallery(variantId, color);
    if (resolved) {
      if (this.#lastGalleryKey === resolved.key || this.dataset.pjGalleryKey === resolved.key) {
        this.#clientApplied = true;
      } else {
        this.#clientApplied = this.#applyGalleryFast(resolved);
      }
    }

    event.promise
      .then(({ detail, variant }) => {
        if (!detail?.html) return;

        // Prefer variant id from the settled promise when sync detail lacked it
        const settledVariantId = this.#normalizeVariantId(
          (variant?.id != null && variant.id) ||
            (detail.resource?.id != null && detail.resource.id) ||
            variantId ||
            ''
        );

        if (!this.#clientApplied && settledVariantId && settledVariantId !== variantId) {
          const settledColor = color || this.#colorFromVariantOptions(variant?.selectedOptions);
          const settled = this.#resolveGallery(settledVariantId, settledColor);
          if (settled && this.#applyGalleryFast(settled)) {
            this.#clientApplied = true;
            return;
          }
        }

        if (this.#clientApplied) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        const currentKey = this.dataset.pjGalleryKey || this.dataset.pjColorGalleryKey;
        const nextKey =
          newMediaGallery instanceof HTMLElement
            ? newMediaGallery.dataset.pjGalleryKey || newMediaGallery.dataset.pjColorGalleryKey
            : undefined;

        if (currentKey && nextKey && currentKey === nextKey) {
          return;
        }

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * @param {{ name: string, value: string }[] | undefined} options
   * @returns {string | null}
   */
  #colorFromVariantOptions(options) {
    if (!Array.isArray(options)) return null;
    for (const option of options) {
      const name = option?.name?.trim()?.toLowerCase?.() ?? '';
      if (name === 'color' || name === 'colour' || name === 'colors' || name === 'colours') {
        return option.value?.trim?.() || null;
      }
    }
    return null;
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialog = this.refs.zoomDialogComponent;
    if (!zoomDialog) return;

    zoomDialog.ensureMediaHydrated();
    const zoomDialogMedia = zoomDialog.refs.media?.[index];
    if (!zoomDialogMedia) return;

    zoomDialog.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
